import { supabaseClient } from '@auth-v2';
import { useMutation, useQuery } from '@tanstack/react-query';

import { UserPreferredUnit } from '@core';
import { fetchUserSettings, fetchUserSettingsWithThrow, LocaleCode, upsertUserSettings, UserSettings } from '@data';
import { currentUserIdAtom, useEnsureAtomValue } from '@store';

import { queryClient } from '../../config/client';
import { queryKeys } from '../../queryKeys';

export const DEFAULT_USER_SETTINGS = {
    settings: {},
    locale: 'en' as LocaleCode,
    userPreferredUnit: 'metric' as UserPreferredUnit,
};

export const useUserSettingsSafe = (selectedUserId?: string) => {
    const userId = useEnsureAtomValue(currentUserIdAtom);
    const userToLoad = selectedUserId ?? userId;
    return useQuery({
        queryKey: queryKeys.user.settings(userToLoad),
        queryFn: () => fetchUserSettings(supabaseClient, userToLoad!),
        enabled: !!userToLoad,
        retry: 3,
    });
};

export const useUserSettings = (selectedUserId?: string) => {
    const userId = useEnsureAtomValue(currentUserIdAtom);
    const userToLoad = selectedUserId ?? userId;
    return useQuery({
        queryKey: queryKeys.user.settings(userToLoad),
        queryFn: () => fetchUserSettingsWithThrow(supabaseClient, userToLoad!),
        enabled: !!userToLoad,
        retry: 3,
    });
};

export const useUpdateUserSettings = (selectedUserId?: string) => {
    /**
     * Determines which user's settings are being updated.
     *
     * - If `selectedUserId` is provided, we are acting on another user (e.g. coach updating a student)
     * - Otherwise, we default to the current logged-in user
     *
     */
    const currentUserId = useEnsureAtomValue(currentUserIdAtom);
    const userContextId = selectedUserId ?? currentUserId;
    const removeUndefinedValues = <T extends Record<string, unknown>>(value: T) =>
        Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined)) as Partial<T>;

    return useMutation({
        mutationFn: async (newSettings: Partial<UserSettings>) => {
            const cachedSettings = queryClient.getQueryData<UserSettings>(queryKeys.user.settings(userContextId));

            const cleanedNewSettings = removeUndefinedValues(newSettings);

            return upsertUserSettings(supabaseClient, {
                ...cachedSettings,
                ...cleanedNewSettings,
                userId: userContextId,
            });
        },
        onMutate: (data) => {
            const key = queryKeys.user.settings(userContextId);
            const oldSettings = queryClient.getQueryData<UserSettings>(key);
            const cleanedData = removeUndefinedValues(data);

            queryClient.setQueryData(queryKeys.user.settings(userContextId), { ...oldSettings, ...cleanedData });
            // If we fail updating we can rollback with this pattern
            return { oldSettings };
        },
        onError: (_err, _data, context) => {
            // We are using the oldSettings to patch the update
            if (context?.oldSettings) {
                queryClient.setQueryData(queryKeys.user.settings(userContextId), context.oldSettings);
            }
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.user.settings(userContextId) });
            queryClient.invalidateQueries({ queryKey: queryKeys.layout.all(userContextId) });
        },
    });
};
